import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, nivelInfo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { NivelAlumnoSelect } from "@/components/NivelAlumnoSelect";
import { NoEstaEnLista } from "@/components/NoEstaEnLista";
import { AltaExpressProfesor } from "@/components/AltaExpressProfesor";
import { emocionInfo } from "@/lib/emociones";
import { Link } from "react-router-dom";
import { Clock, Check, X, Loader2, ClipboardCheck, CalendarDays, Camera } from "lucide-react";

const hoyIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

export default function PasarLista() {
  const { user } = useAuth();
  const [fecha, setFecha] = useState(hoyIso());
  const [data, setData] = useState(null);
  const [activa, setActiva] = useState(null);
  const [lista, setLista] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const load = () =>
    api.get(`/mis-sesiones/hoy?fecha=${fecha}`).then(({ data }) => {
      setData(data);
      setActiva(null);
      setLista(null);
    });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha]);

  const abrir = async (s) => {
    setActiva(s);
    const { data } = await api.get(`/sesiones/${s.id}/asistencia`);
    setLista(data.alumnos);
  };

  const marcar = (id, estado) =>
    setLista((l) => l.map((a) => (a.id === id ? { ...a, estado } : a)));

  const guardar = async () => {
    setGuardando(true);
    try {
      const registros = lista.filter((a) => a.estado).map((a) => ({ alumno_id: a.id, estado: a.estado, nota: "" }));
      const { data } = await api.post(`/sesiones/${activa.id}/asistencia`, { registros });
      toast.success(
        `Lista guardada · ${data.creditos_generados} reposiciones generadas · ${data.clases_descontadas} clases descontadas`
      );
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const esHoy = fecha === hoyIso();
  const esProfesor = user?.role === "profesor";

  return (
    <div data-testid="pasar-lista-page">
      <PageHeader
        title="Pasar lista"
        subtitle="Marca la llegada de cada niño o su falta al empezar la clase"
        action={
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
              className="rounded-lg h-11 w-[170px]" data-testid="pasar-lista-fecha" />
          </div>
        }
      />

      {data && data.vinculado === false && (
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="profesor-no-vinculado">
          <p className="text-sm text-muted-foreground">
            Tu usuario no está vinculado a una ficha de profesor. Pide a administración que cree tu ficha
            en Profesores con este mismo email.
          </p>
        </Card>
      )}

      {data?.festivo && (
        <Card className="rounded-2xl p-5 mb-6 border-amber-500/40 bg-amber-500/10" data-testid="aviso-festivo">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
            Día sin clase: {data.festivo}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            La academia está cerrada esta fecha, por eso no aparecen clases.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4 lg:col-span-1">
          {(data?.sesiones || []).map((s) => (
            <Card key={s.id} onClick={() => abrir(s)} data-testid={`lista-sesion-${s.id}`}
              className={`rounded-2xl p-5 border shadow-sm cursor-pointer transition-all duration-300 hover:-translate-y-[2px] ${
                activa?.id === s.id ? "border-primary bg-accent/40" : "border-border"
              }`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-display font-bold">{s.clase}</p>
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" /> {s.hora_inicio}–{s.hora_fin} · {s.sede}
                  </p>
                </div>
                <Badge variant={s.asistencia_registrada ? "default" : "secondary"}>
                  {s.asistencia_registrada ? "Lista hecha" : "Pendiente"}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {s.registrados}/{s.matriculados} alumnos registrados
              </p>
            </Card>
          ))}
          {data && data.vinculado !== false && data.sesiones.length === 0 && (
            <p className="text-sm text-muted-foreground" data-testid="sin-sesiones-hoy">
              No hay clases programadas para este día.
            </p>
          )}
        </div>

        <Card className="rounded-2xl p-6 border-border shadow-sm lg:col-span-2" data-testid="lista-panel">
          {!activa && <p className="text-sm text-muted-foreground">Selecciona una clase para pasar lista.</p>}
          {activa && !lista && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
          {activa && lista && (
            <>
              <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
                <div>
                  <h3 className="font-display text-xl font-bold">{activa.clase}</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {activa.fecha} · {activa.hora_inicio}–{activa.hora_fin}
                  </p>
                </div>
                <Button className="rounded-full font-semibold" onClick={guardar}
                  disabled={guardando || !lista.length} data-testid="guardar-lista-btn">
                  {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ClipboardCheck className="h-4 w-4 mr-2" /> Guardar</>}
                </Button>
              </div>

              {!esHoy && esProfesor && (
                <p className="text-xs text-muted-foreground mb-4" data-testid="aviso-no-hoy">
                  {fecha > hoyIso()
                    ? "Clase futura: aquí ves quién se espera y ya puedes dejar la lista preparada."
                    : "Clase pasada: puedes completar o corregir la lista."}
                </p>
              )}

              <div className="space-y-3">
                {lista.map((a) => {
                  const nivel = nivelInfo(a.nivel);
                  return (
                    <div key={a.id} className="rounded-xl border border-border p-4 flex items-center justify-between gap-3 flex-wrap"
                      data-testid={`lista-alumno-${a.id}`}>
                      <div>
                        <div className="font-semibold flex items-center gap-2 flex-wrap">
                          {a.emocion && (
                            <span title={`${a.emocion_label || a.emocion}${a.emocion_fecha ? ` · ${a.emocion_fecha}` : ""}`}
                              className="text-lg" data-testid={`emocion-${a.id}`}>
                              {emocionInfo(a.emocion).emoji}
                            </span>
                          )}
                          <span className={`h-2 w-2 rounded-full ${nivel.dot}`} />
                          <Link to={`/admin/ficha/${a.id}`} className="hover:text-primary transition-colors duration-200"
                            data-testid={`ficha-link-${a.id}`}>{a.nombre} {a.apellidos}</Link>
                          {a.aviso_cobro && (
                            <Badge variant="outline" data-testid={`aviso-cobro-${a.id}`}
                              className="rounded-full border-amber-500 text-amber-600 text-[11px] font-semibold">
                              {a.aviso_cobro}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {a.origen === "profesor"
                            ? `Añadido por el profesor${a.anadido_por ? ` (${a.anadido_por})` : ""}${a.nota ? ` · ${a.nota}` : ""}`
                            : a.estado === "reposicion"
                              ? "Viene de reposición"
                              : a.seguro_reposicion ? "Con seguro de recuperación" : `Sin seguro · bono: ${a.clases_disponibles}`}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/admin/ficha/${a.id}?subir=1`} data-testid={`subir-trabajo-${a.id}`}>
                          <Button size="sm" variant="outline" className="rounded-full" title="Subir trabajo">
                            <Camera className="h-4 w-4" />
                          </Button>
                        </Link>
                        <NivelAlumnoSelect alumno={a} esProfesor={esProfesor}
                          onCambio={() => abrir(activa)} />
                        <Button size="sm" data-testid={`llego-${a.id}`}
                          variant={a.estado === "presente" ? "default" : "outline"}
                          className="rounded-full" onClick={() => marcar(a.id, "presente")}>
                          <Check className="h-4 w-4 mr-1.5" /> Llegó
                        </Button>
                        <Button size="sm" data-testid={`falta-${a.id}`}
                          variant={a.estado === "ausente" ? "destructive" : "outline"}
                          className="rounded-full" onClick={() => marcar(a.id, "ausente")}>
                          <X className="h-4 w-4 mr-1.5" /> Falta
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {lista.length === 0 && (
                  <p className="text-sm text-muted-foreground" data-testid="lista-sin-alumnos">
                    No hay alumnos matriculados en esta clase.
                  </p>
                )}
              </div>
              {(!esProfesor || esHoy) && (
                <NoEstaEnLista sesionId={activa.id} onHecho={() => abrir(activa)} />
              )}
              <AltaExpressProfesor sedeId={activa.sede_id} claseSugerida={activa.clase_id}
                onCreado={() => abrir(activa)} />
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
